import type { FormDataTransfer } from './interface'
import { useRuntimeConfig } from '#app'
import { notificationPopup } from '~utils/modal/notify'
import { NumberUtils } from '~/utils'
import { TRANSFER_API } from '~/configs/api'
import { WALLET } from '~/constants/transfer'
import { NEW_ACCOUNT_INDIRECT_BRAND, NEW_ACCOUNT_INDIRECT_BRAND2 } from '~/constants/account'
import { useTransferStore } from '~/stores/transfer.store.ts'
import { useDepositData } from '~composables/deposit/use-deposit-data'
import { modalList } from '~/resources/popup-notification'
import { useDevice } from '#imports'
import { ResponseStatus } from '~/composables/common/interface'
import type { DropdownItem } from '~/composables/common/interface'
import { useUserData } from '~composables/account/use-user-data'
import { useApi } from '~/composables/common/use-api'

export function useTransfer() {
  const { isMobile } = useDevice()
  const runtimeConfig = useRuntimeConfig()
  const { request } = useApi()
  const { currentBalanceFormat } = useUserData()
  const { initListAgency } = useDepositData()
  const isAgencyToMain = ref(false)
  const messageError = ref('')
  const formData = reactive<FormDataTransfer>({
    amount: '',
    betAccount: '',
    totalBalance: '',
    transferAccount: {},
    receiveAccount: {}
  })
  const receiveAccounts = ref([] as DropdownItem[])

  const newWalletItems: DropdownItem[] = [
    {
      label: `Tạo ví ${runtimeConfig.public.INDIRECT_BRAND_NAME} mới`,
      type: WALLET.AGENCY_WALLET,
      value: NEW_ACCOUNT_INDIRECT_BRAND
    },
    {
      label: `Tạo ví ${runtimeConfig.public.INDIRECT_BRAND2_NAME} mới`,
      type: WALLET.AGENCY_WALLET,
      value: NEW_ACCOUNT_INDIRECT_BRAND2
    }
  ]

  const betAccountStore = useTransferStore()

  const betAccounts = computed(() => {
    return betAccountStore.getBetAccounts()
  })

  const creditRate = computed(() => {
    return betAccountStore.getCreditRate()
  })

  const transferAccounts = computed(() => {
    const mainCreditUnit = runtimeConfig.public.MAIN_CREDIT_UNIT
    const credits = betAccountStore.getBetAccounts()
    const dropdown: DropdownItem[] = [
      {
        label: `<span>Ví Chính | <strong>${currentBalanceFormat.value} ${mainCreditUnit}</strong></span>`,
        value: WALLET.MAIN_WALLET,
        type: WALLET.MAIN_WALLET
      }
    ]
    Object.entries(credits).forEach(([indirectBrandName, accounts]) => {
      accounts.forEach(betAccount => {
        dropdown.push({
          label: `<span>Ví ${indirectBrandName} | ${betAccount} <small>(${creditRate.value}K/1 điểm cược)</small>`,
          value: betAccount,
          type: WALLET.AGENCY_WALLET
        })
      })
    })
    return dropdown
  })

  const onChangeInput = (amount: string): void => {
    formData.amount = amount
  }

  const handleTransferAccountChange = (selectItem: DropdownItem): void => {
    formData.receiveAccount = {}
    formData.transferAccount = selectItem
    const mediateAccounts = transferAccounts.value.filter(item => item.type !== selectItem.type)
    if (selectItem.type === WALLET.AGENCY_WALLET) {
      receiveAccounts.value = [...mediateAccounts]
      isAgencyToMain.value = true
      formData.betAccount = selectItem.value as string
    } else {
      receiveAccounts.value = [...mediateAccounts, ...newWalletItems]
      isAgencyToMain.value = false
    }
  }

  const handleReceiveAccountChange = (selectItem: DropdownItem): void => {
    formData.receiveAccount = selectItem
    if (selectItem.type === WALLET.AGENCY_WALLET) {
      formData.betAccount = selectItem.value as string
    }
  }

  async function handleTransfer(): Promise<void> {
    messageError.value = ''
    if (!formData.betAccount) {
      notificationPopup({
        icon: '/assets/images/popup/deposit-error.png',
        title: 'LỖI',
        description: 'Không tìm thấy tài khoản chơi game',
        confirmButtonLink: '',
        cancelButtonText: 'Thử lại'
      })
      return
    }
    const from = isAgencyToMain.value ? WALLET.AGENCY_WALLET : WALLET.MAIN_WALLET
    const to = isAgencyToMain.value ? WALLET.MAIN_WALLET : WALLET.AGENCY_WALLET

    const bodyRequest = {
      amount: NumberUtils.formatMarkToNumber(formData.amount) * 1000,
      from,
      to,
      betAccount: formData.betAccount
    }

    try {
      const { status, message } = await request(TRANSFER_API.TRANSFER, {
        method: 'POST',
        body: bodyRequest
      })
      if (status === ResponseStatus.OK) {
        notificationPopup(modalList.TRASFER_WALLET_SUCCESS)
        resetForm()
        return
      }

      if (status === ResponseStatus.INSUFFICIENT_EXCHANGE_PACKAGE_ERROR) {
        messageError.value = message ?? 'Không thể xác nhận chuyển tiền'
        return
      }
      if (!isMobile) {
        notificationPopup({
          icon: '/assets/images/popup/deposit-error.png',
          title: 'LỖI',
          description: message ?? 'Không thể xác nhận chuyển tiền',
          confirmButtonLink: '',
          cancelButtonText: 'Thử lại'
        })
      }
      messageError.value = message ?? 'Không thể xác nhận chuyển tiền'
    } catch {
      if (isMobile) {
        messageError.value = 'Không thể xác nhận chuyển tiền'
        return
      }
      notificationPopup(modalList.ERROR_SYSTEM)
    }
  }

  function resetForm(): void {
    messageError.value = ''
    formData.amount = ''
  }

  onMounted(initListAgency)

  return {
    formData,
    currentBalanceFormat,
    betAccounts,
    creditRate,
    handleTransfer,
    onChangeInput,
    messageError,
    transferAccounts,
    receiveAccounts,
    handleTransferAccountChange,
    handleReceiveAccountChange
  }
}
